import React, { useEffect, useMemo } from 'react';

import useDepartmentStore from '../../stores/departmentStore';
import { useScope } from '../../context/PermissionContext';
import { STAFF_ROLES } from '../../constants/staff';
import {
  SCOPE_CAPTION_LEAD,
  SCOPE_CAPTION_NAMED_MAX,
  SCOPE_CAPTION_OWN_PATIENTS,
  scopeCaptionScope,
} from '../../constants/admission';

/**
 * useDeskWallViewer — the viewer is a department-walled desk user: a non-admin
 * RECEPTIONIST with >=1 department. The ONE rule for the screens that carry no
 * server flag (Manage Visits, Receptionist dashboard, Manage Appointments — the
 * visit and appointment walls both cover a receptionist). The ADMISSION screens
 * (Manage Admission, IPD page) use the list response's `dept_scoped` instead —
 * the server's word, never a role check.
 */
export function useDeskWallViewer() {
  const { role, isAdmin, viewerDeptIds } = useScope();
  return !isAdmin && role === STAFF_ROLES.RECEPTIONIST
    && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0;
}

function CaptionBody({ className }) {
  const { role, viewerDeptIds } = useScope();
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const deptsLoading = useDepartmentStore((s) => s.isLoading);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  // The org department list (names). Fetched only when missing and not already on
  // its way; the store marks it loaded even on failure, so this cannot loop.
  const haveDepartments = (departments || []).length > 0;
  useEffect(() => {
    if (!haveDepartments && !deptsLoaded && !deptsLoading) fetchDepartments();
  }, [haveDepartments, deptsLoaded, deptsLoading, fetchDepartments]);

  // The viewer's ACTIVE departments, in the org list's order (stable).
  const names = useMemo(() => {
    const own = Array.isArray(viewerDeptIds) ? viewerDeptIds : [];
    return (departments || [])
      .filter((d) => d.active !== false && own.includes(d.dept_id || d.id))
      .map((d) => d.name);
  }, [departments, viewerDeptIds]);

  // Nothing to name yet (edges / list still loading, or only deactivated departments):
  // show nothing rather than "Showing:  + No department + Emergency".
  if (names.length === 0) return null;
  const hidden = names.length > SCOPE_CAPTION_NAMED_MAX ? names.join(', ') : undefined;
  return (
    <div
      className={`text-[11.5px] text-slate-600 dark:text-slate-300 ${className}`}
      data-testid="scope-caption"
      title={hidden}
    >
      {SCOPE_CAPTION_LEAD} <b className="font-semibold text-slate-700 dark:text-slate-200">{scopeCaptionScope(names)}{role === STAFF_ROLES.DOCTOR ? SCOPE_CAPTION_OWN_PATIENTS : ''}</b>
    </div>
  );
}

/**
 * ScopeCaption (admission-access S13) — the quiet line that says which patients a
 * department-walled desk user's list covers: "Showing: Ortho, ENT +1 more + No
 * department + Emergency". `scoped` is the caller's word that the wall applies
 * (admission screens: the response's `dept_scoped === true`; the others:
 * `useDeskWallViewer()`); anything but `true` renders nothing and mounts nothing,
 * so unwalled viewers cost no store reads. Hovering "+n more" lists every department.
 */
export default function ScopeCaption({ scoped, className = '' }) {
  if (scoped !== true) return null;
  return <CaptionBody className={className} />;
}
